/**
 * Profile layout - provides profile context for child routes.
 *
 * Loads profile data and makes it available to nested routes like channels.
 */

import {
  prefetchProfileBySlug,
  profileSlugResolveQueryOptions,
} from "@/lib/queries/profile";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import {
  ORG_PROFILE_SLUG_PREFIX,
  findProfileSlugRedirect,
} from "@/lib/utils/profile-slug-redirect";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import type { OrgRouteContext } from "../../route";

import { z } from "zod";

export interface ProfileRouteContext extends OrgRouteContext {
  profile: {
    id: string;
    organizationId: string;
    name: string;
    slug: string;
    description: string | null;
    avatarUrl: string | null;
    metadata: string | null;
    email: string | null;
    emailVerified: boolean | null;
    emailVerifiedAt: Date | null;
    emailSource: string | null;
    createdAt: Date;
    updatedAt: Date;
  };
}

const profileSearchSchema = z.object({
  tab: z
    .enum(["profile", "channels", "payment", "address", "settings", "history"])
    .optional()
    .catch("profile"),
  error: z.string().optional(),
});

export const Route = createFileRoute("/(org)/o/$orgSlug/profiles/$profileSlug")(
  {
    validateSearch: (search) => profileSearchSchema.parse(search),
    beforeLoad: async ({ context, params, location }) => {
      const orgContext = context as OrgRouteContext;
      const { profileSlug } = params;
      if (
        !ensureOrgContext(
          orgContext.organization,
          "(org)/o.$orgSlug/profiles/$profileSlug/route.tsx#beforeLoad",
          { orgSlug: params.orgSlug, profileSlug },
        )
      ) {
        throw redirect({
          to: "/",
          search: { error: "organization_not_found" },
        });
      }

      let profile;
      let fetchError: unknown = null;

      try {
        profile = await prefetchProfileBySlug(
          context.queryClient,
          orgContext.organization.id,
          profileSlug,
        );
      } catch (error) {
        fetchError = error;
      }

      // Handle not found - either no profile or tRPC 404 error
      if (
        !profile ||
        (fetchError instanceof Error &&
          "data" in fetchError &&
          (fetchError as { data?: { code?: string } }).data?.code ===
            "NOT_FOUND")
      ) {
        // A retired slug (EPD-11640): keep the tab/search, swap the slug.
        const slugRedirect = await findProfileSlugRedirect({
          location,
          prefix: ORG_PROFILE_SLUG_PREFIX,
          slug: profileSlug,
          resolve: (slug) =>
            context.queryClient.fetchQuery(
              profileSlugResolveQueryOptions(orgContext.organization.id, slug),
            ),
        });
        if (slugRedirect) {
          throw redirect({ href: slugRedirect, replace: true });
        }

        throw redirect({
          to: "/o/$orgSlug/profiles",
          params: { orgSlug: params.orgSlug },
          search: { error: "profile_not_found" },
        });
      }

      // Re-throw other errors
      if (fetchError) {
        throw fetchError;
      }

      return {
        profile,
      } as Omit<ProfileRouteContext, keyof OrgRouteContext>;
    },
    component: ProfileLayout,
  },
);

function ProfileLayout() {
  return <Outlet />;
}
